import {Dimensions, StyleSheet} from 'react-native';
import {H2Text, H5Text} from '@buncha/components/core/text';
import {LogInStates, OnFleetEvents} from '@buncha/config/onfleet/types';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import font, {scaleFonts} from '@buncha/appStyles/style/font';
import {getWidth, inputFormattedNumber} from '@buncha/utils/common';

import {Analytics} from '@buncha/config/analytics/analytics';
import AppModal from '@buncha/common/components/CustomModal/AppModal';
import {BottomButton} from '@buncha/components/composites/Button';
import BottomSheet from '@buncha/components/composites/bottomSheet';
import {Box} from '@buncha/components/core/view';
import CrossEyeIcon from '@buncha/components/core/icon/CrossEyeIcon';
import {EventType} from '@buncha/config/analytics/type';
import EyeIcon from '@buncha/components/core/icon/EyeIcon';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import Input from '@buncha/onBoarding/components/Input/Input';
import {Modules} from '@buncha/utils/types';
import {OnfleetEventHandler} from '@buncha/config/onfleet/service';
import {SpinnerComponent} from '@buncha/components/core/loader';
import {StorageKeys} from '@buncha/config/storage/type';
import {ToastType} from '@buncha/components/composites/notification/type';
import {Touchable} from '@buncha/components/core/touchable';
import onfleetService from '@buncha/config/onfleet';
import {storage} from '@buncha/config/storage';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useColorThemeType} from '@buncha/appStyles/hooks/theme';
import {useGetLocale} from '@buncha/localization/hooks';
import {useToastMessage} from '@buncha/components/composites/notification';

interface Props {
  open: boolean;
  onClose: () => void;
  afterLogin?: () => void;
  source: string;
}

const width = Number(getWidth(Dimensions.get('screen').width));

const DriverLoginModal = (props: Props) => {
  const {onClose, open, source} = props;
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const [showSuccessToast] = useToastMessage(ToastType.Success);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);
  const [provisioningTime, setProvisioningTime] = useState<string>('');
  const [prefix, setPrefix] = useState('+1');
  const theme = useColorThemeType('main');
  const {title, body, button} =
    useGetLocale(Modules.aboutShift, HomeScreens.AboutShift) ?? {};

  const isDriverLoggedIn = Boolean(
    useAppSelector(gState => gState.home.driver?.id),
  );

  const togglePrefix = useCallback(
    () => setPrefix(p => (p === '+1' ? '+91' : '+1')),
    [],
  );

  const handlePhoneChange = useCallback((text: string) => {
    if (Number(text) || text.length === 0) setPhoneNumber(text);
  }, []);

  const handlePasswordChange = useCallback((text: string) => {
    setPassword(text);
  }, []);

  const updateStates = useCallback(
    (state: LogInStates, message: string) => {
      let authenticate = false;
      let provisioning = '';
      switch (state) {
        case LogInStates.authenticating:
          authenticate = true;
          break;
        case LogInStates.logInFailed:
          showErrorToast(message);
          break;
        case LogInStates.provisioning:
          provisioning = message;
          break;
        case LogInStates.loggedIn:
          onfleetService?.fetchDriver();
          showSuccessToast(message);
          break;
        default:
          showErrorToast(message);
      }

      setAuthenticating(authenticate);
      setProvisioningTime(provisioning);
    },
    [showErrorToast, showSuccessToast],
  );

  const loginToOnfleet = useCallback(async () => {
    setLoading(true);
    const res = await onfleetService.login(
      prefix + phoneNumber,
      password,
      updateStates,
    );
    if (!res) {
      showErrorToast('Invalid credentials');
    } else {
      onfleetService?.fetchDriver();
      if (props?.afterLogin) props?.afterLogin();
      Analytics.eventWithProps('Onfleet login', {
        type: EventType.cta,
        source: source,
        phoneNumber: prefix + phoneNumber,
      });
    }
    setLoading(false);
    setProvisioningTime('');
    setAuthenticating(false);
    onClose();
  }, [
    onClose,
    password,
    phoneNumber,
    prefix,
    props,
    showErrorToast,
    source,
    updateStates,
  ]);

  const returnLetterSpacing = useMemo(() => {
    if (phoneNumber) return scaleFonts(3.4);
    return 0;
  }, [phoneNumber]);

  const getDisabledStatus = useMemo(() => {
    return !phoneNumber.length || !password.length;
  }, [password.length, phoneNumber.length]);

  const init = useCallback(async () => {
    setPhoneNumber(
      (await storage.getItem(StorageKeys.onfleetPhoneNumber)) ?? '',
    );
    setPrefix(
      (await storage.getItem(StorageKeys.onfleetPhoneNumberPrefix)) ?? '+1',
    );
    setPassword((await storage.getItem(StorageKeys.onfleetPassword)) ?? '');
  }, []);

  useEffect(() => {
    init();

    OnfleetEventHandler.addListener(OnFleetEvents.provisioning, message => {
      const baseText = body?.verifyOnfleet || '';
      const updatedText = baseText.replace('{message}', message);
      setProvisioningTime(updatedText);
    });

    return () => {
      OnfleetEventHandler.removeAllListeners(OnFleetEvents.provisioning);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open)
      Analytics.eventWithProps('Onfleet login modal', {
        type: EventType.modal,
        source: source,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const rightComponentHandler = useMemo(() => {
    return (
      <Touchable style={styles.eyeIcon} onPress={() => setShowPass(!showPass)}>
        <If condition={showPass}>
          <CrossEyeIcon
            fill={theme?.background.blue[300]}
            height={30}
            width={30}
            viewBox="0 -3 30 30"
          />
        </If>
        <If condition={!showPass}>
          <EyeIcon
            fill={theme?.background.blue[300]}
            height={30}
            width={30}
            viewBox="0 -3 30 30"
          />
        </If>
      </Touchable>
    );
  }, [showPass, theme?.background.blue]);

  const formattedNumber = inputFormattedNumber(phoneNumber);

  return (
    <Fragment>
      <AppModal
        isOpen={Boolean(provisioningTime) && !isDriverLoggedIn}
        hideBottomCta
        hideTopCta
        modalBody={
          <Box>
            <H5Text>{provisioningTime}</H5Text>
            <Box>
              <SpinnerComponent size={30} />
            </Box>
          </Box>
        }
      />
      <BottomSheet open={open} onClose={onClose}>
        <Box style={styles.header}>
          <H2Text
            style={[
              styles.headerText,
              {
                color: theme?.text.blue[300],
                fontFamily: font.fonts['Mortise-X-Bold'],
              },
            ]}>
            {title?.loginToOnfleet}
          </H2Text>
        </Box>
        <Input
          editable
          closeIcon={theme?.text?.blue[300]}
          debounce={false}
          defaultValue={formattedNumber}
          value={phoneNumber}
          autoCorrect={false}
          colorThemeType="default"
          keyboardType="number-pad"
          maxLength={14}
          placeholder="Phone Number"
          clearInput={true}
          textValue={phoneNumber}
          containerStyle={[
            styles.inputContainer,
            {borderColor: theme?.text?.gray[200]},
          ]}
          leftComponent={
            <Touchable delayLongPress={1000} onLongPress={togglePrefix}>
              <H5Text
                style={[
                  styles.leftComponent,
                  {
                    color: theme?.text?.gray[100],
                    fontFamily: font.fonts['Mortise-Bold'],
                  },
                ]}>
                {prefix}
              </H5Text>
            </Touchable>
          }
          style={[
            {
              backgroundColor: theme?.background?.white[100],
              fontSize: scaleFonts(16),
              color: theme?.text?.gray[100],
              letterSpacing: returnLetterSpacing,
            },
          ]}
          onChangeText={handlePhoneChange}
        />
        <Input
          secureTextEntry={!showPass}
          editable
          closeIcon={theme?.text?.blue[300]}
          debounce={false}
          defaultValue={password}
          value={password}
          autoCorrect={false}
          colorThemeType="default"
          keyboardType="default"
          placeholder="Password"
          clearInput={true}
          textValue={password}
          rightComponent={rightComponentHandler}
          containerStyle={[
            styles.inputContainer,
            {borderColor: theme?.text?.gray[200]},
          ]}
          style={[
            {
              backgroundColor: theme?.background?.white[100],
              fontSize: scaleFonts(16),
              color: theme?.text?.gray[100],
              letterSpacing: returnLetterSpacing,
            },
          ]}
          onChangeText={handlePasswordChange}
        />
        <BottomButton
          style={[styles.buttonContainer, {width: width * 0.95}]}
          title={authenticating ? button?.authenticating : button?.login}
          disabled={getDisabledStatus || authenticating}
          onPress={loginToOnfleet}
          isLoading={loading || authenticating}
          loadingIndicator={<SpinnerComponent />}
        />
      </BottomSheet>
    </Fragment>
  );
};

export default React.memo(DriverLoginModal);

export const styles = StyleSheet.create({
  headerText: {
    textAlign: 'left',
  },
  leftComponent: {marginLeft: scaleFonts(20), fontWeight: '600'},
  eyeIcon: {
    marginRight: 5,
  },
  buttonContainer: {
    marginBottom: 0,
  },
  inputContainer: {
    height: 60,
    marginHorizontal: 10,
  },
  header: {width: '100%', paddingLeft: 10, marginBottom: 20},
  centeredView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 22,
  },
});
